import { ReactChild, ReactChildren, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import { faEye, faPrint } from '@fortawesome/pro-light-svg-icons';
import { useDispatch } from 'hooks';
import { groupBy, sortBy, values } from 'lodash';
import { useTranslation } from 'react-i18next';
import { fetchHolidays } from 'redux/actionCreators';
import styled from 'styled-components';

import { PublicationInfo, RosterDay } from './types';
import { Interval, PlanUnit } from 'components/types';

import { formatISOString } from 'utils/date';

import { getLatest } from 'RestAPIs/cost-center/publication';

import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';

import { FontAwesomeSvgIcon, Select } from 'components/shared';
import DateInterval from 'components/shared/DateInterval';
import PlanUnitSelector from 'components/shared/PlanUnitSelector';

import HeaderTableCell from './HeaderTableCell';
import { FIRST_CELL_WIDTH } from './const';

export const HeaderStyles = styled.div`
  .main-nav {
    margin-bottom: 10px;
  }

  .filters {
    padding-left: 0 !important;
    vertical-align: bottom !important;
  }

  .planunit-selector {
    .MuiAutocomplete-root {
      min-width: calc(${FIRST_CELL_WIDTH} - 8px);
    }
  }

  .side-container {
    width: 50%;
  }

  .print-roster-btn {
    margin: 0 10px;
  }

  .mode-selector {
    width: 220px;
    margin-left: 10px;
  }

  .publication-info {
    width: 300px;
    color: red;
    display: inline-block;
  }
`;

type Props = {
  lastSelectedPlanUnitsIds?: (string | number)[] | null;
  onPlanUnitSelected: (selectedPlanUnits: PlanUnit[]) => void;
  onPrintRoster: () => void;
  interval: Interval;
  onIntervalChanged: (interval: Interval) => void;
  rosterDays: RosterDay[];
  calendarView: 'week' | 'month';
  rosterView: 'employees' | 'duties';
  onRosterViewChanged?: (rosterView: 'employees' | 'duties') => void;
  children: ReactChild | ReactChild[] | ReactChildren | ReactChildren[];
};

export default function Header(props: Props) {
  const {
    lastSelectedPlanUnitsIds,
    onPlanUnitSelected,
    onPrintRoster,
    interval,
    onIntervalChanged,
    rosterDays,
    calendarView,
    rosterView,
    onRosterViewChanged,
    children
  } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const [publicationInfo, setPublicationInfo] =
    useState<PublicationInfo | null>(null);

  const loadPublicationInfo = (value: PlanUnit[]) => {
    getLatest(value.map(({ id }) => id)).then(({ data }) => {
      setPublicationInfo(data);
    });
  };

  const onSelectedPlanUnit = (value: PlanUnit[]) => {
    setPublicationInfo(null);
    if (value.length === 1) {
      loadPublicationInfo(value);
    }
    onPlanUnitSelected(value);
  };

  const weeks = sortBy(
    values(groupBy(rosterDays, 'weekNr')),
    (arr) => arr[0].date
  );

  const minDate = dayjs('2015-01-01').toDate();
  const maxDate = dayjs().add(2, 'year').toDate();

  useEffect(() => {
    dispatch(fetchHolidays());
  }, [dispatch]);

  return (
    <HeaderStyles>
      <div className="main-nav d-flex v-center between">
        <div className="side-container d-flex v-bottom">
          <PlanUnitSelector
            hideGroups
            onSelected={onSelectedPlanUnit}
            interval={interval}
            doNotPersist
            useEmployeePlanunits
            defaultValue={
              lastSelectedPlanUnitsIds?.map(
                (uniqId) => ({ uniqId }) as PlanUnit
              ) || undefined
            }
          />
        </div>
        <DateInterval
          defaultInterval={interval}
          onSelected={onIntervalChanged}
          view={calendarView}
          minDate={minDate}
          maxDate={maxDate}
        />
        <div className="side-container d-flex end v-center">
          <Tooltip
            title={
              <div>
                <div>
                  {publicationInfo?.lastPublishDate && (
                    <>
                      <span
                        style={{ width: 110, display: 'inline-block' }}
                        className="publication-info"
                      >
                        <strong>{t('publishedUntil')}</strong>:{' '}
                      </span>
                      {formatISOString(publicationInfo.lastPublishDate)}
                    </>
                  )}
                  {!publicationInfo?.lastPublishDate && t('rosterNotPublished')}
                </div>
                <div>
                  {publicationInfo?.visibleUntilDate && (
                    <>
                      <span
                        style={{ width: 110, display: 'inline-block' }}
                        className="publication-info"
                      >
                        <strong>{t('visibleUntil')}</strong>:{' '}
                      </span>
                      {formatISOString(publicationInfo.visibleUntilDate)}
                    </>
                  )}
                  {!publicationInfo?.visibleUntilDate && t('visibilityNotSet')}
                </div>
              </div>
            }
            arrow
          >
            <IconButton size="small" color="primary">
              <FontAwesomeSvgIcon icon={faEye} fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('printRoster') || ''} arrow>
            <IconButton
              onClick={onPrintRoster}
              size="small"
              color="primary"
              className="print-roster-btn"
            >
              <FontAwesomeSvgIcon icon={faPrint} fontSize="small" />
            </IconButton>
          </Tooltip>
          {onRosterViewChanged && (
            <Select
              label={t('view')}
              onSelected={onRosterViewChanged}
              options={[
                {
                  value: 'employees',
                  label: t('employees')
                },
                {
                  value: 'duties',
                  label: t('duties')
                }
              ]}
              valueProp={rosterView}
              className="mode-selector"
            />
          )}
        </div>
      </div>
      <Table className="roster-grid">
        <TableHead>
          <TableRow>
            <TableCell rowSpan={2} className="filters">
              {children}
            </TableCell>
            {calendarView === 'month' &&
              weeks.map((week) => (
                <TableCell key={week[0].weekNr} colSpan={week.length}>
                  <div>{`${t('week')} ${week[0].weekNr}`}</div>
                </TableCell>
              ))}
          </TableRow>
          <TableRow>
            {rosterDays.map((rosterDay) => (
              <HeaderTableCell
                rosterDay={rosterDay}
                key={+rosterDay.date}
                view={calendarView}
              />
            ))}
          </TableRow>
        </TableHead>
      </Table>
    </HeaderStyles>
  );
}
